/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "AuthContext";
import { useMemo } from "react";
import PropTypes from "prop-types";
import { Card } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import ArticleIcon from "@mui/icons-material/Article";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";

import { documentFields } from "layouts/interviewSection/constant/documentFields";
import { API_URL } from "config";

function UploadDocuments({ value = {}, onChange, onUpdate, loading }) {
  const { permissions: ctxPerms = [] } = useAuth();

  const storedPerms = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return [];
    }
  }, []);

  const permSet = useMemo(() => {
    const perms = ctxPerms.length ? ctxPerms : storedPerms;
    return new Set(perms.map(p => (typeof p === "string" ? p : p.name)));
  }, [ctxPerms, storedPerms]);

  const canSection = permSet.has("UserProfile.UploadDocuments");
  const canView = (key) => permSet.has(`UserProfile.UploadDocuments.${key}`);

  const [previewUrls, setPreviewUrls] = useState({});
  const [fileInputKeys, setFileInputKeys] = useState({});

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "Confirm Save",
    message: "Are you sure you want to save the uploaded documents?",
  });

  useEffect(() => {
    const initialKeys = {};
    documentFields.forEach(({ key }) => {
      initialKeys[key] = `file-input-${key}-${Date.now()}`;
    });
    setFileInputKeys(initialKeys);
  }, []);

  const getFileTypeDetails = (file) => {
    if (!file) return {
      type: 'none',
      icon: InsertDriveFileIcon,
      color: '#b0b0b0',
      label: 'No file',
      bgColor: '#f8f9fa'
    };

    let fileName = '';
    let fileType = '';

    if (typeof file === 'string') {
      fileName = file.split('/').pop().toLowerCase();
      fileType = 'url';
    } else if (file instanceof File) {
      fileName = file.name.toLowerCase();
      fileType = file.type;
    } else {
      return {
        type: 'other',
        icon: InsertDriveFileIcon,
        color: '#757575',
        label: 'File',
        bgColor: '#f5f5f5'
      };
    }

    const extension = fileName.split('.').pop();

    // Simplified file type detection
    if (extension === 'pdf' || fileType === 'application/pdf') {
      return {
        type: 'pdf',
        icon: PictureAsPdfIcon,
        color: '#d32f2f',
        label: 'PDF',
        bgColor: '#ffebee'
      };
    }
    else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension) ||
      fileType.includes('image/')) {
      return {
        type: 'image',
        icon: ImageIcon,
        color: '#9c27b0',
        label: 'Image',
        bgColor: '#f3e5f5'
      };
    }
    else if (['doc', 'docx'].includes(extension) || fileType.includes('word')) {
      return {
        type: 'doc',
        icon: ArticleIcon,
        color: '#2b579a',
        label: 'Word',
        bgColor: '#e8f4fd'
      };
    }
    else if (['xls', 'xlsx'].includes(extension) || fileType.includes('excel')) {
      return {
        type: 'excel',
        icon: ArticleIcon,
        color: '#217346',
        label: 'Excel',
        bgColor: '#e8f5e8'
      };
    }
    else if (extension === 'txt' || fileType === 'text/plain') {
      return {
        type: 'txt',
        icon: TextSnippetIcon,
        color: '#4caf50',
        label: 'Text',
        bgColor: '#e8f5e8'
      };
    }
    else {
      return {
        type: 'other',
        icon: InsertDriveFileIcon,
        color: '#757575',
        label: 'Document',
        bgColor: '#f5f5f5'
      };
    }
  };

  const getFileUrl = (file) => {
    if (!file) return null;
    if (typeof file === 'string') {
      return file.startsWith('http') ? file : `${API_URL}${file}`;
    }
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return null;
  };


  useEffect(() => {
    return () => {
      Object.keys(previewUrls).forEach(key => {
        const url = previewUrls[key];
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  useEffect(() => {
    const objectUrls = {};
    const initialUrls = {};

    Object.entries(value).forEach(([key, file]) => {
      if (typeof file === "string") {
        initialUrls[key] = file;
      } else if (file instanceof File) {
        if (!objectUrls[key]) {
          objectUrls[key] = URL.createObjectURL(file);
        }
        initialUrls[key] = objectUrls[key];
      }
    });

    setPreviewUrls(initialUrls);

    return () => {
      Object.values(objectUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [value]);

  const handleFileUpload = useCallback(
    (key) => (file) => {
      if (!(file instanceof File)) return;

      const updated = {
        ...value,
        [key]: file,
      };
      onChange?.(updated);

      setPreviewUrls((prev) => ({
        ...prev,
        [key]: URL.createObjectURL(file),
      }));

      setFileInputKeys((prev) => ({
        ...prev,
        [key]: `file-input-${key}-${Date.now()}`,
      }));
    },
    [value, onChange]
  );

  const handleFileRemove = (key) => {
    const updated = { ...value };
    delete updated[key];
    onChange?.(updated);

    if (previewUrls[key] && previewUrls[key].startsWith('blob:')) {
      URL.revokeObjectURL(previewUrls[key]);
    }

    setPreviewUrls((prev) => {
      const newUrls = { ...prev };
      delete newUrls[key];
      return newUrls;
    });
  };

  const showConfirmationDialog = () => {
    setConfirmDialog({
      open: true,
      title: "Confirm Save",
      message: "Are you sure you want to save the uploaded documents?",
    });
  };

  const handleCancelSave = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleConfirmSave = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
    onUpdate?.();
  };

  const FilePreview = ({ file, fallbackUrl, key, label, onRemove }) => {
    const url = getFileUrl(file) || fallbackUrl;
    const fileDetails = getFileTypeDetails(file || fallbackUrl);
    const IconComponent = fileDetails.icon;

    // Fixed size container
    const previewStyle = {
      width: '100px',
      height: '100px',
      margin: '0 auto',
      position: 'relative'
    };

    if (!url) {
      return (
        <div style={previewStyle} className="d-flex flex-column align-items-center justify-content-center">
          <div style={{
            width: '70px',
            height: '70px',
            borderRadius: '8px',
            backgroundColor: fileDetails.bgColor,
            border: `2px dashed ${fileDetails.color}50`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <IconComponent style={{ fontSize: 32, color: fileDetails.color, opacity: 0.5 }} />
          </div>
          <div className="mt-2" style={{ fontSize: '11px', color: '#999' }}>
            No file
          </div>
        </div>
      );
    }

    // For images
    if (fileDetails.type === 'image') {
      return (
        <div style={previewStyle}>
          <div className="position-relative h-100">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="d-block h-100"
            >
              <img
                src={url}
                alt={label}
                className="img-fluid rounded h-100 w-100"
                style={{
                  objectFit: 'cover',
                  border: `2px solid ${fileDetails.color}30`
                }}
              />
            </a>
            {onRemove && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove();
                }}
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '22px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '12px',
                  zIndex: 10
                }}
                title="Remove"
              >
                ×
              </button>
            )}
          </div>
        </div>
      );
    }

    // For other files
    return (
      <div style={previewStyle}>
        <div className="position-relative h-100">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="d-block h-100 text-decoration-none"
          >
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              backgroundColor: fileDetails.bgColor,
              border: `2px solid ${fileDetails.color}30`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <IconComponent style={{ fontSize: 36, color: fileDetails.color, marginBottom: '4px' }} />
              <div style={{
                fontSize: '10px',
                color: fileDetails.color,
                fontWeight: '600',
                textAlign: 'center',
                padding: '0 4px',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {fileDetails.label}
              </div>
            </div>
          </a>
          {onRemove && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '22px',
                height: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              title="Remove"
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  };

  if (!canSection) {
    return null;
  }

  return (
    <Card id="uploadDocuments" className="p-3" sx={{ borderRadius: '12px' }}>
      <SoftTypography variant="h5" mb={2} fontWeight="medium">
        Upload Documents
      </SoftTypography>

      <div className="row g-2">
        {documentFields.map(({ label, key }) => {
          const canViewDoc = canView(`${key}.view`);
          const canUploadDoc = canView(`${key}.upload`);
          const canPreviewDoc = canView(`${key}.preview`);
          const canDeleteDoc = canView(`${key}.delete`);

          if (!canViewDoc) return null;

          const file = value[key];

          console.log("👉 KEY:", key);
          console.log("👉 VALUE:", value[key]);
          console.log("👉 FULL VALUE:", value);

          console.log("👉 HAS KEY:", key in value);
          const fallbackUrl = typeof value[key] === 'string' ? getFileUrl(value[key]) : null;

          return (
            <div key={key} className="col-md-3 mb-3">
              <SoftBox
                bgColor="white"
                borderRadius="md"
                className="p-2"
                style={{
                  border: '1px solid #e0e0e0',
                  height: '100%'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <SoftTypography
                    variant="caption"
                    fontWeight="medium"
                    style={{
                      color: '#424242',
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '70%'
                    }}
                  >
                    {label}
                  </SoftTypography>

                  <div className="d-flex align-items-center">
                    {canUploadDoc && (
                      <UploadButton
                        key={fileInputKeys[key]}
                        onFileSelect={handleFileUpload(key)}
                        // accept={
                        //   key.includes('photo') || key.includes('image') 
                        //     ? "image/*" 
                        //     : ".pdf,.doc,.docx,.txt,.xls,.xlsx"
                        // }
                        inputId={`upload-${key}`}
                      />
                    )}

                    {canDeleteDoc && file && (
                      <SoftButton
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => handleFileRemove(key)}
                        style={{
                          minWidth: '28px',
                          width: '28px',
                          height: '28px',
                          padding: '4px',
                          marginLeft: '4px'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </SoftButton>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <FilePreview
                    file={file}
                    fallbackUrl={fallbackUrl}
                    key={key}
                    label={label}
                    onRemove={canDeleteDoc ? () => handleFileRemove(key) : null}
                  />
                </div>

                {file && (
                  <div className="text-center">
                    <a
                      href={getFileUrl(file) || fallbackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '11px',
                        color: '#1976d2',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <DownloadIcon fontSize="inherit" />
                      View File
                    </a>
                  </div>
                )}
              </SoftBox>
            </div>
          );
        })}
      </div>

      <div className="d-flex justify-content-end mt-3">
        {permSet.has("UserProfile.UploadDocuments.save") && (
          <SoftButton
            disabled={loading || !value?.is_uploaddocuments}
            variant="gradient"
            color="info"
            onClick={showConfirmationDialog}
          >
            {loading ? "Saving..." : "Save"}
          </SoftButton>
        )}
      </div>

      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelSave}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirmation-dialog-title" sx={{ pb: 1 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            onClick={handleCancelSave}
            color="secondary"
            variant="outlined"
            size="small"
            sx={{ minWidth: '80px' }}
          >
            Cancel
          </SoftButton>
          <SoftButton
            onClick={handleConfirmSave}
            color="info"
            variant="gradient"
            size="small"
            autoFocus
            disabled={loading || !value?.is_uploaddocuments}
            sx={{ minWidth: '80px' }}
          >
            {loading ? "Saving..." : "Confirm"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

UploadDocuments.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  onUpdate: PropTypes.func,
};

function UploadButton({ onFileSelect, accept, inputId }) {
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && typeof onFileSelect === "function") {
      onFileSelect(file);
    }
    event.target.value = '';
  };

  return (
    <>
      <input
        type="file"
        accept={accept}
        style={{ display: "none" }}
        id={inputId}
        onChange={handleFileChange}
        key={inputId}
      />
      <label htmlFor={inputId}>
        <SoftButton
          variant="outlined"
          color="info"
          size="small"
          component="span"
          sx={{
            minWidth: '28px',
            width: '28px',
            height: '28px',
            padding: '4px'
          }}
        >
          <UploadIcon fontSize="small" />
        </SoftButton>
      </label>
    </>
  );
}

UploadButton.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  accept: PropTypes.string,
  inputId: PropTypes.string.isRequired,
};

UploadButton.defaultProps = {
  accept: "*/*",
};

export default UploadDocuments;
